import * as React from "react";
import Button from "@mui/material/Button";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import {styled} from "@mui/material";
import Loading from "../../components/loading";
import {toast} from "react-toastify";
import {gql} from "@apollo/client";
import {mutate} from "../../lib/apollo";
import downloadCsv from "../../lib/downloadCsv";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

const REQUIRED_HEADERS = [
    "hp_campaign_id",
    "monthly_cap",
    "daily_cap",
    "cost_per_lead",
    "pay_per_impression",
] as const;

interface CapRow {
    hpCampaignId: number;
    monthlyCap: number | null;
    dailyCap: number | null;
    costPerLead: number | null;
    payPerImpression: number | null;
}

interface BulkResult {
    updatedCount: number;
    deletedQueueCount: number;
    messages: string[];
    errors: string[];
}

function parseOptionalNumber(value: string, field: string, lineNo: number): number | null {
    const trimmed = value.trim();
    if (trimmed === "") {
        return null;
    }
    if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
        throw new Error(`Line ${lineNo}: Invalid ${field}: '${value}'`);
    }
    return Number(trimmed);
}

export function parseCapsTsv(text: string): {rows: CapRow[]; parseErrors: string[]} {
    const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((line) => line.trim() !== "");
    if (lines.length < 2) {
        return {rows: [], parseErrors: ["File must include a header row and at least one data row"]};
    }

    const delimiter = lines[0].includes("\t") ? "\t" : ",";
    const headers = lines[0].split(delimiter).map((h) => h.trim());
    const indexes: Record<string, number> = {};
    for (const required of REQUIRED_HEADERS) {
        const idx = headers.indexOf(required);
        if (idx < 0) {
            return {rows: [], parseErrors: [`Missing header field: ${required}`]};
        }
        indexes[required] = idx;
    }

    const rows: CapRow[] = [];
    const parseErrors: string[] = [];

    for (let i = 1; i < lines.length; i++) {
        const lineNo = i + 1;
        const cells = lines[i].split(delimiter);
        try {
            const idRaw = (cells[indexes.hp_campaign_id] ?? "").trim();
            if (!/^\d+$/.test(idRaw)) {
                throw new Error(`Invalid hp_campaign_id: '${idRaw}'`);
            }
            rows.push({
                hpCampaignId: Number(idRaw),
                monthlyCap: parseOptionalNumber(cells[indexes.monthly_cap] ?? "", "monthly_cap", lineNo),
                dailyCap: parseOptionalNumber(cells[indexes.daily_cap] ?? "", "daily_cap", lineNo),
                costPerLead: parseOptionalNumber(cells[indexes.cost_per_lead] ?? "", "cost_per_lead", lineNo),
                payPerImpression: parseOptionalNumber(
                    cells[indexes.pay_per_impression] ?? "",
                    "pay_per_impression",
                    lineNo
                ),
            });
        } catch (e: any) {
            parseErrors.push(`Line ${lineNo}: ${e?.message || e}`);
        }
    }

    return {rows, parseErrors};
}

export default function SetCapsPage() {
    const [runNow, setRunNow] = React.useState(true);
    const [loading, setLoading] = React.useState(false);
    const [result, setResult] = React.useState<BulkResult | null>(null);
    const [parseErrors, setParseErrors] = React.useState<string[]>([]);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        document.title = "Set Caps | DN";
    }, []);

    const handleDownloadTemplate = () => {
        downloadCsv("set_caps_template.csv", [[...REQUIRED_HEADERS]]);
    };

    const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        // Allow re-selecting the same file
        event.target.value = "";
        if (!file) {
            return;
        }

        setLoading(true);
        setResult(null);
        setParseErrors([]);

        try {
            const text = await file.text();
            const {rows, parseErrors: localErrors} = parseCapsTsv(text);
            setParseErrors(localErrors);

            if (rows.length === 0) {
                toast.error(localErrors[0] || "No valid rows found");
                setLoading(false);
                return;
            }

            if (localErrors.length > 0) {
                toast.warning(`${localErrors.length} invalid row(s) skipped`);
            }

            const q = gql`
                mutation HpCampaignCapsBulkUpdate($runNow: Boolean!, $rows: [HpCampaignCapUpdateInput!]!) {
                    hpCampaignCapsBulkUpdate(runNow: $runNow, rows: $rows) {
                        updatedCount
                        deletedQueueCount
                        messages
                        errors
                    }
                }
            `;

            const response = await mutate(q, {runNow, rows});
            if (!response?.hpCampaignCapsBulkUpdate) {
                setLoading(false);
                return;
            }

            const data = response.hpCampaignCapsBulkUpdate as BulkResult;
            setResult(data);
            toast.success(
                `${data.updatedCount} campaign(s) updated` +
                    (data.deletedQueueCount ? `, ${data.deletedQueueCount} queue lead(s) deleted` : "")
            );
        } catch (e: any) {
            toast.error(e?.message || "Failed to process file");
        }

        setLoading(false);
    };

    return (
        <>
            <h2>Set Caps</h2>
            <p>
                Upload a tab- or comma-delimited file. Header row must include (case sensitive):
                <Box sx={{display: "flex", alignItems: "center", gap: 1, mt: 1, flexWrap: "wrap"}}>
                    <code>hp_campaign_id	monthly_cap	daily_cap	cost_per_lead	pay_per_impression</code>
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadTemplate}
                    >
                        Download header CSV
                    </Button>
                </Box>
            </p>

            <Accordion sx={{maxWidth: 800, mb: 2, boxShadow: "none", border: "1px solid", borderColor: "divider"}}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight={600}>
                        Q: What happens if you leave a column blank?
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Typography component="div" sx={{fontSize: 14}}>
                        <p style={{marginTop: 0}}>
                            Blank does <b>not</b> mean zero. It means “no value provided” for that column
                            (<code>monthly_cap</code>, <code>daily_cap</code>, <code>cost_per_lead</code>, or{" "}
                            <code>pay_per_impression</code>). Behavior depends on the <b>When</b> setting:
                        </p>
                        <p>
                            <b>Now</b> — the blank column is left unchanged on the live campaign (or portal redirect).
                            Only columns you fill in are updated. Example: if you set a new monthly cap but leave
                            daily blank, the daily cap stays as it is today.
                        </p>
                        <p>
                            <b>1st of month</b> — the blank column is stored as <code>NULL</code> in the staged
                            next-month caps table. Those rows are applied later by the monthly job, not immediately.
                            A <code>NULL</code> staged value means that field has no override to apply from this upload.
                        </p>
                        <p style={{marginBottom: 0}}>
                            Queue trimming (deleting pending leads when caps drop) only runs in <b>Now</b> mode, and
                            only when both <code>monthly_cap</code> and <code>daily_cap</code> are provided as numbers.
                        </p>
                    </Typography>
                </AccordionDetails>
            </Accordion>

            {loading ? (
                <Loading />
            ) : (
                <Box sx={{display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap"}}>
                    <FormControl size="small" sx={{minWidth: 180}}>
                        <InputLabel id="run-time-label">When</InputLabel>
                        <Select
                            labelId="run-time-label"
                            label="When"
                            value={runNow ? "0" : "1"}
                            onChange={(e) => setRunNow(e.target.value === "0")}
                        >
                            <MenuItem value="0">Now</MenuItem>
                            <MenuItem value="1">1st of month</MenuItem>
                        </Select>
                    </FormControl>

                    <Button
                        component="label"
                        variant="contained"
                        startIcon={<CloudUploadIcon />}
                    >
                        Upload file
                        <VisuallyHiddenInput
                            ref={fileInputRef}
                            type="file"
                            accept=".txt,.tsv,.csv,text/plain"
                            onChange={handleFile}
                        />
                    </Button>
                </Box>
            )}

            {parseErrors.length > 0 ? (
                <Alert severity="warning" sx={{mt: 2, maxWidth: 800}}>
                    <div>Parse issues ({parseErrors.length}):</div>
                    <Box component="pre" sx={{m: 0, mt: 1, whiteSpace: "pre-wrap"}}>
                        {parseErrors.slice(0, 20).join("\n")}
                        {parseErrors.length > 20 ? `\n…and ${parseErrors.length - 20} more` : ""}
                    </Box>
                </Alert>
            ) : null}

            {result ? (
                <Box sx={{mt: 2, maxWidth: 800}}>
                    <Alert severity={result.errors.length ? "warning" : "success"} sx={{mb: 2}}>
                        Updated {result.updatedCount}
                        {result.deletedQueueCount
                            ? `; deleted ${result.deletedQueueCount} queued lead(s)`
                            : ""}
                        {result.errors.length ? `; ${result.errors.length} error(s)` : ""}
                    </Alert>
                    {result.messages.length ? (
                        <Box component="pre" sx={{whiteSpace: "pre-wrap", fontSize: 13}}>
                            {result.messages.join("\n")}
                        </Box>
                    ) : null}
                    {result.errors.length ? (
                        <Alert severity="error" sx={{mt: 1}}>
                            <Box component="pre" sx={{m: 0, whiteSpace: "pre-wrap"}}>
                                {result.errors.join("\n")}
                            </Box>
                        </Alert>
                    ) : null}
                </Box>
            ) : null}
        </>
    );
}
